import { Dispatch, SetStateAction } from "react";

import { isEmpty } from "lodash";
import {
  SampleAvailabilityIcons,
  SampleAvailabilityMessages
} from "../../enums";
import { ClassificationEnum } from "../../lib/enums";
import * as notifications from "../../notifications/notifications";
import { consts, enums } from "../../shim/jsbridge";
import {
  BaseHashListItem,
  CloudSearchCompareFieldsResponse,
  CloudSearchDownloadStatusResponse,
  CloudSearchExtractedResponse,
  CloudSearchHashesResponse,
  CloudSearchLocalResponse,
  CloudSearchRca2Response,
  CloudSearchRca2ResponseItem,
  CloudSearchRlDataResponse,
  CloudSearchRlDataResponseItem,
  CompareSetDiff,
  CurrentGridDataState,
  FileSimilaritySample,
  IQueryParams,
  QueryParamsKey
} from "./@types";
import { ALL_CLASSIFICATIONS_OPTION_ENUM } from "./consts";
import { Sample } from "../samples/@types/expanded_local_details";

const {
  searchRecordsType,
  threat_status: threatStatus,
  state: processingState
} = enums || {};

export const availabilityGridIconDisplay = (sample: FileSimilaritySample) => {
  const exceedsMaxFileSize =
    sample.file_size > consts.ticloud_max_fetch_file_size;

  const isExtractedFile = !sample.upload_id;

  if (!sample.cloud) {
    if (isExtractedFile) {
      return [
        SampleAvailabilityIcons.LOCAL_EXTRACTED,
        SampleAvailabilityMessages.LOCAL_EXTRACTED
      ];
    }
    return [
      SampleAvailabilityIcons.LOCAL_TOP_LEVEL,
      SampleAvailabilityMessages.LOCAL_TOP_LEVEL
    ];
  }
  if (sample.is_not_found) {
    return [];
  }
  if (sample.downloadable && !exceedsMaxFileSize) {
    return [SampleAvailabilityIcons.CLOUD, SampleAvailabilityMessages.CLOUD];
  }
  if (exceedsMaxFileSize) {
    return [
      SampleAvailabilityIcons.MAXIMUM,
      SampleAvailabilityMessages.AVAILABLE_AND_MAXIMUM
    ];
  }
  return [
    SampleAvailabilityIcons.CLOUD_UNAVAILABLE,
    SampleAvailabilityMessages.CLOUD_UNAVAILABLE
  ];
};

export const stringifyParams = ({
  params,
  excludedKeys
}: {
  params: IQueryParams;
  excludedKeys?: string[];
}): string => {
  if (!params) return "";
  return Object.keys(params)
    .map(key =>
      excludedKeys?.includes(key)
        ? ""
        : `${key}=${params[key as QueryParamsKey]}`
    )
    .join("&");
};

export const sseGridErrorHandler = ({
  error,
  setErrorMessage
}: {
  error?: Record<string, unknown>;
  setErrorMessage: Dispatch<SetStateAction<string>>;
}) => {
  if (error?.validation_message) {
    notifications.show({
      message: error.validation_message,
      level: "error",
      sticky: true
    });
  } else if (error?.connection_error_message) {
    notifications.show({
      message: error.connection_error_message,
      level: "error",
      sticky: true
    });
  } else {
    setErrorMessage("There was a problem completing your request.");
    notifications.show({
      message:
        "We’re sorry, but there was a problem completing your request. If the problem persists please contact support.",
      level: "error",
      sticky: true
    });
  }
};

export const handleRhaGridWarning = ({
  data,
  baseHashList,
  locationFilter,
  showWarning,
  setShowWarning
}: {
  data: CloudSearchHashesResponse;
  baseHashList: BaseHashListItem[];
  locationFilter: string | null;
  showWarning: boolean;
  setShowWarning: Dispatch<SetStateAction<boolean>>;
}) => {
  let message;
  if (data.count > 0 && baseHashList.length === 0) {
    if (locationFilter === searchRecordsType.ALL) {
      message =
        "All samples exist locally, but local classifications differ from Spectra Intelligence.";
    } else if (location === searchRecordsType.LOCAL) {
      message = "Local classifications differ from Spectra Intelligence.";
    } else if (location === searchRecordsType.TICLOUD) {
      message = "All Spectra Intelligence samples exist locally.";
    }
  } else if (data.filtered_by_threat_status) {
    message =
      "Count differs because one or more Spectra Intelligence classifications differ from local classification.";
  }
  if (message !== undefined && showWarning) {
    notifications.show({
      message,
      level: "warning"
    });
    setShowWarning(false);
  }
};

const isHashNotFound = ({
  sample,
  isRca2Fetched,
  isRlDataEmpty
}: {
  sample: FileSimilaritySample;
  isRca2Fetched?: boolean;
  isRlDataEmpty?: boolean;
}) =>
  sample.cloud &&
  (sample.is_rl_data_empty || Boolean(isRlDataEmpty)) &&
  (sample.is_rca2_fetched || Boolean(isRca2Fetched)) &&
  sample.threat_status === threatStatus.UNKNOWN;

export const populateInitialSampleData = (hashes: string[]) =>
  hashes.map(hashValue => ({
    state: processingState.PROCESSED,
    id: hashValue,
    sha1: hashValue,
    cloud: true,
    file_name: hashValue,
    loading: true,
    threat_status: threatStatus.UNKNOWN,
    is_not_found: false,
    date_submission_time: "",
    created: "",
    downloadable: false,
    file_size: 0,
    file_count: 0
  }));

export const transformWithLocalData = (
  localData: CloudSearchLocalResponse,
  currentGridData: CurrentGridDataState
) =>
  currentGridData.map((sample: FileSimilaritySample) => {
    if (sample.sha1 in localData) {
      const localSampleData = localData[sample.sha1];
      if (isEmpty(localSampleData)) return sample;

      return {
        ...sample,
        ...localSampleData,
        file_name: localSampleData.filename,
        cloud: false,
        date_submission_time: new Date(localSampleData.created),
        string_submissions_user: localSampleData.username,
        is_not_found: false,
        loading: false
      };
    } else {
      return sample;
    }
  });

export const transformWithExtractedFileData = (
  extractedFilesData: CloudSearchExtractedResponse,
  currentGridData: CurrentGridDataState
) =>
  currentGridData.map((sample: FileSimilaritySample) => {
    if (sample.sha1 in extractedFilesData) {
      const extractedData = extractedFilesData[sample.sha1];
      if (isEmpty(extractedData)) return sample;

      return {
        ...sample,
        ...extractedData,
        cloud: false,
        created: new Date(extractedData.created),
        extracted: true,
        is_not_found: false,
        loading: false,
        file_name: extractedData.filename
      };
    } else {
      return sample;
    }
  });

export const transformWithRca2Data = (
  rca2Data: CloudSearchRca2Response,
  currentGridData: CurrentGridDataState
) =>
  currentGridData.map((sample: FileSimilaritySample) => {
    const rca2DataSample: CloudSearchRca2ResponseItem = rca2Data[sample.sha1];

    if (!sample.cloud) {
      // get_rca2 seeds every hash with {}, so a truthiness check here would
      // rebuild every local row on every message with two undefined keys.
      if (isEmpty(rca2DataSample)) {
        return sample;
      }
      // A local row owns its classification and must not take the cloud's, but
      // the AV counts exist nowhere else, so take those.
      return {
        ...sample,
        scanner_count: rca2DataSample.scanner_count,
        scanner_match: rca2DataSample.scanner_match
      };
    }

    if (!rca2DataSample) {
      return sample;
    }

    const { classification } = rca2DataSample;
    return {
      ...sample,
      ...rca2DataSample,
      // return unknown when we don't have classification
      // this would typically happen when we search on cloud by unsupported hash_types
      // supported hash_types are: md5, sha1, sha256
      threat_status: classification
        ? threatStatus[classification.toUpperCase()]
        : threatStatus.UNKNOWN,
      // RCA2 carries a cloud row's threat name as `result`; a local row
      // keeps its own, from the local results.
      threat_name: rca2DataSample.result,
      loading: false,
      created: rca2DataSample.first_seen
        ? new Date(rca2DataSample.first_seen)
        : "--",
      is_rca2_fetched: !!classification,
      is_not_found: isHashNotFound({
        sample,
        isRca2Fetched: !!classification
      })
    };
  });

export const transformWithRlData = (
  rlData: CloudSearchRlDataResponse,
  currentGridData: CurrentGridDataState
) =>
  currentGridData.map((sample: FileSimilaritySample) => {
    if (sample.sha1 in rlData && sample.cloud) {
      const rlDataSample: CloudSearchRlDataResponseItem = rlData[sample.sha1];
      return {
        ...sample,
        ...rlDataSample,
        file_size: rlDataSample.file_size,
        file_count: rlDataSample.file_count,
        type_display: rlDataSample.type_display,
        loading: false,
        is_rl_data_empty: isEmpty(rlDataSample),
        is_not_found: isHashNotFound({
          sample,
          isRlDataEmpty: isEmpty(rlDataSample)
        })
      };
    } else {
      return sample;
    }
  });

/**
 * Cloud-only hashes arrive as an empty object and are left untouched, so the
 * matrix reads them as no data rather than as empty values.
 */
export const transformWithCompareFieldsData = (
  compareData: CloudSearchCompareFieldsResponse,
  currentGridData: CurrentGridDataState
) =>
  currentGridData.map((sample: FileSimilaritySample) => {
    const compareFields = compareData[sample.sha1];
    if (isEmpty(compareFields)) {
      return sample;
    }
    return { ...sample, ...compareFields };
  });

export const transformWithDownloadStatus = (
  downloadStatusData: CloudSearchDownloadStatusResponse,
  currentGridData: CurrentGridDataState
) =>
  currentGridData.map((sample: FileSimilaritySample) => {
    if (sample.sha1 in downloadStatusData && sample.cloud) {
      return {
        ...sample,
        ...{
          downloadable: downloadStatusData[sample.sha1]
        }
      };
    } else {
      return sample;
    }
  });

export const rhaFiltersFromSearchParams = (searchParams: URLSearchParams) => {
  const classification = searchParams.get("threat_status");
  return {
    // Tested as a string: "0" is Unknown, a real filter.
    classificationFilter: classification
      ? Number(classification)
      : ALL_CLASSIFICATIONS_OPTION_ENUM,
    locationFilter: searchParams.get("records_type") || searchRecordsType.ALL
  };
};

export const cleanUknownClassificationString = (classification: string) =>
  classification === ClassificationEnum.unknown ? "unknown" : classification;

/** Duplicates within a list are collapsed first so the counts stay honest. */
export const diffSets = (
  main: string[] = [],
  other: string[] = []
): CompareSetDiff => {
  const mainLookup = new Set(main);
  const otherLookup = new Set(other);
  const uniqueMain = Array.from(mainLookup);
  const uniqueOther = Array.from(otherLookup);

  const mainChips = uniqueMain.map(text => ({
    text,
    shared: otherLookup.has(text)
  }));
  const otherChips = uniqueOther.map(text => ({
    text,
    shared: mainLookup.has(text)
  }));
  const shared = mainChips.filter(chip => chip.shared).length;

  return {
    mainChips,
    otherChips,
    shared,
    onlyMain: uniqueMain.length - shared,
    onlyOther: uniqueOther.length - shared
  };
};

/** Sorted so a diff between two samples is stable. */
export const enabledCapabilities = (sample: Sample): string[] => {
  const capabilities = sample.details?.data?.capabilities_descriptions;
  if (!capabilities) {
    return [];
  }
  return Object.keys(capabilities)
    .filter(name => Boolean(capabilities[name as keyof typeof capabilities]))
    .sort();
};
